import { useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Settings, Mail, Users, HardHat, Upload, Calendar, CheckCircle2,
  ArrowRight, Info, Globe, QrCode, Eye, Shield
} from "lucide-react";

const operatingModes = [
  { id: "internal", label: "Builder manages permits internally", desc: "Your team handles council submissions, RFIs, and proof collection directly.", icon: HardHat },
  { id: "external", label: "External consultant handles permits", desc: "A planner or consultant manages the permit process. You receive the handover.", icon: Users },
  { id: "readiness", label: "Builder mainly uses readiness and release control", desc: "Focus on stage readiness, trade release, and proof tracking. Start from consultant handover.", icon: Shield },
];

const setupModules = [
  { id: "inbox", label: "Connect Outlook or Gmail", desc: "Auto-capture council emails and classify them.", icon: Mail, options: ["Outlook 365", "Gmail / Google Workspace"], status: "connected" },
  { id: "consultants", label: "Add key consultant contacts", desc: "Engineer, architect, planner, traffic consultant.", icon: Users, status: "3 added" },
  { id: "trades", label: "Add trade contacts", desc: "Concreter, framer, plumber, electrician, roofer.", icon: HardHat, status: "5 added" },
  { id: "reviewer", label: "Choose who reviews proof", desc: "Builder PM, office admin, or nominated reviewer.", icon: Eye, status: "Sarah Chen" },
  { id: "sender", label: "Choose who sends requests", desc: "Who usually sends council responses and consultant requests.", icon: ArrowRight, status: "Sarah Chen" },
  { id: "qr", label: "Site QR uploads", desc: "Enable QR code uploads for site supervisors.", icon: QrCode, status: "Enabled" },
  { id: "calendar", label: "Calendar connection", desc: "Create reminders and events from readiness states.", icon: Calendar, options: ["Google Calendar", "Outlook Calendar"], status: "Google Calendar" },
];

export default function BuilderSetup() {
  const [selectedMode, setSelectedMode] = useState("internal");

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="builder-setup-page">
        <Card className="border-blue-100 bg-blue-50/30">
          <CardContent className="py-4 px-5 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-blue-800">Set up how your business works</p>
              <p className="text-xs text-blue-600 mt-0.5">You can start at different points in the workflow. If consultants handle permits, use easypermit from consultant handover onward.</p>
            </div>
          </CardContent>
        </Card>

        <div>
          <h3 className="font-heading font-semibold text-base text-slate-900 mb-3">Operating mode</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {operatingModes.map((mode) => (
              <Card
                key={mode.id}
                className={`border-2 cursor-pointer transition-all hover:shadow-md ${selectedMode === mode.id ? "border-blue-500 bg-blue-50/30 shadow-md" : "border-slate-200 hover:border-slate-300"}`}
                onClick={() => setSelectedMode(mode.id)}
                data-testid={`mode-${mode.id}`}
              >
                <CardContent className="pt-5 pb-4 px-5">
                  <div className="flex items-center justify-between mb-3">
                    <mode.icon className={`w-6 h-6 ${selectedMode === mode.id ? "text-blue-600" : "text-slate-400"}`} />
                    {selectedMode === mode.id && <CheckCircle2 className="w-5 h-5 text-blue-600" />}
                  </div>
                  <h4 className="font-heading font-semibold text-sm text-slate-900 mb-1">{mode.label}</h4>
                  <p className="text-xs text-slate-500">{mode.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-heading font-semibold text-base text-slate-900 mb-3">Setup modules</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {setupModules.map((mod) => (
              <Card key={mod.id} className="border-slate-200" data-testid={`setup-${mod.id}`}>
                <CardContent className="py-4 px-5 flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center shrink-0">
                    <mod.icon className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-slate-900">{mod.label}</h4>
                    <p className="text-xs text-slate-400">{mod.desc}</p>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] shrink-0">{mod.status}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button className="bg-slate-900 text-white hover:bg-slate-800 gap-2" data-testid="save-setup-btn">
            <CheckCircle2 className="w-4 h-4" /> Save setup
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
